// @version 3.0
// @link https://piccalil.li/tutorial/build-a-fully-responsive-progressively-enhanced-burger-menu/

class ToggleMenu extends HTMLElement {
	constructor() {
		super();

		const self = this;

		this.state = new Proxy(
			{
				status: "open",
				enabled: false,
			},
			{
				set(state, key, value) {
					const oldValue = state[key];

					state[key] = value;
					if (oldValue !== value) {
						self.processStateChange();
					}
					return state;
				},
			}
		);
	}

	get maxWidth() {
		return parseInt(this.getAttribute("max-width") || 99999, 10);
	}

	getFocusableElements(parent) {
		if (!parent) {
			console.warn("You need to pass a parent HTMLElement");
			return [];
		}

		return parent.querySelectorAll(
			'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
		);
	}

	connectedCallback() {
		this.trigger = this.querySelector(".toggle-menu-trigger");
		this.triggerClasses = this.trigger.classList;
		this.triggerMarkup = this.trigger.innerHTML;

		this.panel = this.querySelector(".toggle-menu-panel");
		this.panelClasses = this.panel.classList;
		this.panelMarkup = this.panel.innerHTML;

		this.render();

		if (this.hasAttribute("max-width")) {
			const observer = new ResizeObserver((observedItems) => {
				const { contentRect } = observedItems[0];
				this.state.enabled = contentRect.width <= this.maxWidth;
			});

			// We want to watch the parent like a hawk
			observer.observe(this.parentNode);
		} else {
			this.state.enabled = true;
		}
	}

	render() {
		this.innerHTML = `
			<button class="${this.triggerClasses}" type="button" aria-controls="toggle-menu-panel" id="toggle-menu-trigger">
				${this.triggerMarkup} 
			</button>
			<div class="${this.panelClasses}" data-toggled-by="toggle-menu-trigger" id="toggle-menu-panel">
				${this.panelMarkup}
			</div>
		`;

		this.postRender();
	}

	postRender() {
		this.trigger = this.querySelector(".toggle-menu-trigger");
		this.panel = this.querySelector(".toggle-menu-panel");
		this.label = this.querySelector(".toggle-menu-label");

		if (this.trigger && this.panel) {
			this.classList.add("toggle-menu");

			this.toggle();

			this.trigger.addEventListener("click", (event) => {
				event.preventDefault();

				this.toggle();
			});

			window.addEventListener("hashchange", (event) => {
				// console.log(event);

				this.toggle("closed");
			});

			document.addEventListener("keydown", (event) => {
				if (this.state.status === "closed") {
					return;
				}

				if (event.key === "Escape") {
					this.toggle("closed");
				}
			});

			document.addEventListener("focusin", () => {
				if (!this.contains(document.activeElement)) {
					this.toggle("closed");
				}
			});

			//
			document.addEventListener("click", (event) => {
				// Do nothing if the menu is already closed
				if (this.state.status === "closed") {
					return;
				}

				// Do nothing for clicks in this element
				if (event.target.closest("toggle-menu")) {
					return;
				}

				// Otherwise, close the menu
				this.toggle("closed");
			});

			return;
		}

		this.innerHTML = this.menuMarkup;
	}

	setLabelText() {
		const labelOpen = this.getAttribute("label-open") ? this.getAttribute("label-open") : "Close menu";
		const labelClosed = this.getAttribute("label-closed") ? this.getAttribute("label-closed") : "Open menu";

		// console.log(this.state.status);

		switch (this.state.status) {
			case "closed":
				this.label.textContent = labelClosed;
				this.trigger.setAttribute("title", labelClosed);
				this.trigger.setAttribute("aria-label", labelClosed);
				break;
			case "open":
				this.label.textContent = labelOpen;
				this.trigger.setAttribute("title", labelOpen);
				this.trigger.setAttribute("aria-label", labelOpen);
				break;
		}
	}

	setAriaExpanded() {
		switch (this.state.status) {
			case "closed":
				this.removeAttribute("open");
				this.trigger.setAttribute("aria-expanded", "false");
				this.panel.setAttribute("aria-hidden", "true");
				break;
			case "open":
				this.setAttribute("open", "");
				this.trigger.setAttribute("aria-expanded", "true");
				this.panel.setAttribute("aria-hidden", "false");
				break;
		}

		// If the menu isn't enabled, set aria-hidden="false"
		if (!this.state.enabled) {
			this.panel.setAttribute("aria-hidden", "false");
		}
	}

	toggle(forcedStatus) {
		if (forcedStatus) {
			if (this.state.status === forcedStatus) {
				return;
			}

			this.state.status = forcedStatus;
		} else {
			this.state.status = this.state.status === "closed" ? "open" : "closed";
		}
	}

	processStateChange() {
		this.setAttribute("status", this.state.status);
		this.setAttribute("enabled", this.state.enabled ? "true" : "false");

		document.documentElement.setAttribute("data-menu-active", this.state.enabled);
		document.documentElement.setAttribute("data-menu-status", this.state.status);

		this.setLabelText();

		this.setAriaExpanded();
	}
}

if ("customElements" in window) {
	customElements.define("toggle-menu", ToggleMenu);
}
